import { forwardRef, useEffect, useRef, useState } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import styled from 'styled-components';

import { downloadCsv, entriesToCsv, exportFileName } from './entryCsv';
import { EntryRowCells, EntryTr, SelectCheckbox } from './EntryRow';
import {
  DangerButton,
  DangerOutlineButton,
  FormActions,
  GhostButton,
} from './formControls';

import type { TranslationMemoryEntry } from '@src/repositories/TranslationMemoryEntryRepository';
import type { TableComponents } from 'react-virtuoso';

type Status = 'idle' | 'loading' | 'ready' | 'error';

// Bulk-bar slide duration (ms), shared by the CSS transition and the JS
// deferred-unmount timer so the bar stays mounted until it finishes sliding up.
const BAR_ANIM_MS = 200;

// Approximate sticky-header height, added to the measured list height so the
// scroller shrinks to fit small collections (capped at max-height).
const HEADER_H = 40;

interface Props {
  /** Label of the selected collection ("Default" or the collection name). */
  collectionName: string;
  /** Stable key of the selected collection — clears the bulk selection when it
   *  changes (PLAT-10873). Falls back to the display name. */
  collectionKey?: string;
  /** Entries already scoped to the collection and filtered by the shared bar. */
  entries: TranslationMemoryEntry[];
  /** Whether the collection has any entries before search/filters (empty vs
   *  no-match messaging). */
  hasAnyEntries: boolean;
  /** In the "All translations" view, show a per-row Collection column so each
   *  entry's collection is legible (it's redundant in a single-collection view). */
  showCollection: boolean;
  /** Resolve an entry's collection id (null = Default) to its display name. */
  resolveCollectionName: (collectionId: string | null) => string;
  status: Status;
  error: string | null;
  /** Open the edit modal (owned by the section, alongside create). */
  onEditEntry: (entry: TranslationMemoryEntry) => void;
  onDelete: (id: string) => Promise<string | null>;
  retry: () => void;
}

// Per-row data threaded through TableVirtuoso's `context` so the row/table/
// scroller components (defined once, at module scope) can read live selection +
// callbacks without remounting the virtualizer.
interface RowContext {
  selectedIds: Set<string>;
  showCollection: boolean;
  columnCount: number;
  resolveCollectionName: (collectionId: string | null) => string;
  toggleRow: (id: string) => void;
  onEditEntry: (entry: TranslationMemoryEntry) => void;
  onDelete: (id: string) => Promise<string | null>;
}

// Percentage column widths (no fixed px column) so the table can never exceed
// 100% and force a horizontal scrollbar; term/translation trimmed to make room.
const Colgroup = ({ showCollection }: { showCollection: boolean }) =>
  showCollection ? (
    <colgroup>
      <col style={{ width: '4%' }} />
      <col style={{ width: '22%' }} />
      <col style={{ width: '22%' }} />
      <col style={{ width: '12%' }} />
      <col style={{ width: '11%' }} />
      <col style={{ width: '11%' }} />
      <col style={{ width: '10%' }} />
      <col style={{ width: '8%' }} />
    </colgroup>
  ) : (
    <colgroup>
      <col style={{ width: '4%' }} />
      <col style={{ width: '26%' }} />
      <col style={{ width: '26%' }} />
      <col style={{ width: '12%' }} />
      <col style={{ width: '12%' }} />
      <col style={{ width: '10%' }} />
      <col style={{ width: '10%' }} />
    </colgroup>
  );

// Reserve a consistent height so switching collections (which have different
// row counts) doesn't resize the pane / shift the layout.
const Body = styled.div`
  min-height: 360px;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

// Fills whatever height the bulk bar leaves within the fixed-height Body, so the
// table scroll area (not the whole block) is what shrinks when the bar opens.
const TableRegion = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

// Collapsible wrapper that slides the bulk bar down/up instead of jumping.
// Animating grid-template-rows 0fr→1fr transitions the TRUE content height (no
// max-height guess, so it never stalls part-way); the child clips as it grows.
// Owns its own bottom gap so the collapsed state leaves no space above the
// table.
const BulkBarSlot = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  margin-bottom: ${({ $open }) => ($open ? '10px' : '0')};
  transition:
    grid-template-rows ${BAR_ANIM_MS}ms ease,
    opacity ${BAR_ANIM_MS}ms ease,
    margin-bottom ${BAR_ANIM_MS}ms ease;

  > * {
    overflow: hidden;
    min-height: 0;
  }
`;

// Toolbar above the table, visible only when ≥1 row is selected — the selected
// count on the left, Export / Delete on the right (or the delete confirmation).
const BulkBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
`;

const BulkCount = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const BulkActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Inline delete confirmation that replaces the bar's contents — mirrors the
// single-row confirm idiom (danger border, message, Cancel / Delete).
const BulkConfirm = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;

  > p {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  color: var(--destructive);
`;

// Cap the table height so long glossaries scroll inside the pane (anchored under
// the toolbar) instead of growing the page; the collection list caps to match.
// Doubles as TableVirtuoso's Scroller.
const TableScroll = styled.div`
  overflow: auto;
  max-height: 55vh;
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
`;

const TableEl = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  > thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--background);
    text-align: left;
    padding: 8px 12px;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semi-bold);
    color: var(--text-muted);
    /* box-shadow (not border-bottom) so the divider stays attached to the
       sticky header — a collapsed border scrolls away with the body. */
    box-shadow: inset 0 -1px 0 var(--border);
    white-space: nowrap;
  }

  /* Center the select-all checkbox over the row checkboxes; bottom-aligned (plus
     the control's own nudge) so it sits just under the header label. */
  > thead th.select {
    text-align: center;
    vertical-align: bottom;
    width: 1%;
  }

  > tbody tr:last-child {
    border-bottom: none;
  }
`;

const EmptyCell = styled.td`
  padding: 32px 12px;
  text-align: center;
  font-size: var(--text-sm);
  color: var(--text-secondary);

  button {
    background: none;
    border: none;
    color: var(--primary);
    cursor: pointer;
    font-size: inherit;
    padding: 0;
  }
`;

// TableVirtuoso wiring: only the visible ~20 rows are ever in the DOM, so a
// selection toggle re-renders (and the bulk-bar animation reflows) a handful of
// rows instead of the whole glossary. Defined once at module scope (Virtuoso
// remounts if `components` changes identity); live state flows via `context`.
const VIRTUOSO_COMPONENTS: TableComponents<TranslationMemoryEntry, RowContext> =
  {
    Scroller: forwardRef(function Scroller(
      { context: _context, ...props },
      ref,
    ) {
      return <TableScroll {...props} ref={ref} />;
    }),
    Table: ({ context, children, ...props }) => (
      <TableEl {...props}>
        <Colgroup showCollection={context.showCollection} />
        {children}
      </TableEl>
    ),
    TableRow: ({ item, context, ...props }) => (
      <EntryTr
        {...props}
        data-selected={context.selectedIds.has(item.id) || undefined}
      />
    ),
  };

const renderRow = (
  _index: number,
  entry: TranslationMemoryEntry,
  context: RowContext,
) => (
  <EntryRowCells
    entry={entry}
    columnCount={context.columnCount}
    collectionName={
      context.showCollection
        ? context.resolveCollectionName(entry.collectionId)
        : undefined
    }
    selected={context.selectedIds.has(entry.id)}
    onToggleSelected={() => context.toggleRow(entry.id)}
    onEdit={() => context.onEditEntry(entry)}
    onDelete={() => context.onDelete(entry.id)}
  />
);

// Rows rendered before the scroller is measured, so the table paints content on
// the first frame instead of a blank pane. Doubles as the virtualizer's remount
// key — see `initialCount` below.
const INITIAL_ROWS = 24;

const EntryTable = ({
  collectionName,
  collectionKey,
  entries,
  hasAnyEntries,
  showCollection,
  resolveCollectionName,
  status,
  error,
  onEditEntry,
  onDelete,
  retry,
}: Props) => {
  const hasRows = entries.length > 0;
  // +1 for the leading selection column.
  const columnCount = (showCollection ? 7 : 6) + 1;
  const initialCount = Math.min(entries.length, INITIAL_ROWS);

  // Bulk selection (PLAT-10873). The set can hold ids no longer visible (a
  // filtered-out row) — every action derives its targets from `entries`, so
  // filtering always scopes export/delete to the visible rows.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set<string>(),
  );
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  // Measured list height (from Virtuoso) → the scroller shrinks to fit small
  // collections, capped at the styled max-height for large ones.
  const [listHeight, setListHeight] = useState<number>();

  // Start with nothing selected, and clear the selection when the collection
  // changes. Keyed on the collection (not `entries`) so search typing — which
  // changes `entries` — doesn't wipe out the user's manual toggles.
  const resetKey = collectionKey ?? collectionName;
  const lastKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (status !== 'ready') return;
    if (lastKeyRef.current === resetKey) return;
    lastKeyRef.current = resetKey;
    setSelectedIds(new Set());
    setConfirmingBulk(false);
    setBulkError(null);
  }, [resetKey, status]);

  const selectedEntries = entries.filter(e => selectedIds.has(e.id));
  const selectedCount = selectedEntries.length;
  const allSelected = hasRows && selectedCount === entries.length;
  const someSelected = selectedCount > 0 && !allSelected;

  // Slide the bulk bar down on the first selection and up when it empties (vs a
  // jump). It is mounted in the SAME render the selection opens (via `barOpen`)
  // so the 0fr→1fr slide actually plays, and kept mounted through the slide-up
  // afterwards (`barVisible`) — showing the frozen count while it closes.
  const barOpen = selectedCount > 0;
  const [barVisible, setBarVisible] = useState(false);
  const renderBar = barOpen || barVisible;
  const shownCountRef = useRef(0);
  if (barOpen) shownCountRef.current = selectedCount;
  const shownCount = barOpen ? selectedCount : shownCountRef.current;
  useEffect(() => {
    if (barOpen) {
      setBarVisible(true);
      return;
    }
    const timer = setTimeout(() => setBarVisible(false), BAR_ANIM_MS + 20);
    return () => clearTimeout(timer);
  }, [barOpen]);

  const toggleRow = (id: string) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(entries.map(e => e.id)));

  const handleExport = () => {
    const csv = entriesToCsv(selectedEntries, resolveCollectionName);
    downloadCsv(exportFileName(collectionName, selectedEntries), csv);
  };

  const runBulkDelete = async () => {
    setBulkBusy(true);
    setBulkError(null);
    const targets = [...selectedEntries];
    let failed = 0;
    for (const target of targets) {
      const message = await onDelete(target.id);
      if (message) {
        failed += 1;
      } else {
        setSelectedIds(prev => {
          const next = new Set(prev);
          next.delete(target.id);
          return next;
        });
      }
    }
    setBulkBusy(false);
    if (failed > 0) {
      setBulkError(
        `Couldn't delete ${failed} of ${targets.length} ${
          targets.length === 1 ? 'entry' : 'entries'
        }.`,
      );
    } else {
      setConfirmingBulk(false);
    }
  };

  const rowContext: RowContext = {
    selectedIds,
    showCollection,
    columnCount,
    resolveCollectionName,
    toggleRow,
    onEditEntry,
    onDelete,
  };

  const renderHeaderRow = () => (
    <tr>
      <th className="select">
        <SelectCheckbox
          checked={allSelected}
          indeterminate={someSelected}
          disabled={!hasRows}
          onChange={toggleAll}
          aria-label="Select all entries"
        />
      </th>
      <th>Source term</th>
      <th>Translation</th>
      {showCollection && <th>Collection</th>}
      <th>Source</th>
      <th>Target</th>
      <th>Origin</th>
      <th aria-label="Actions" />
    </tr>
  );

  // Fix the block height to the table's natural (content) height, capped at the
  // max — so when the bulk bar slides in, the table flexes SHORTER to absorb it
  // instead of pushing the block (and everything below, e.g. Import history)
  // down. Height is derived from content, not selection, so it's stable as the
  // bar toggles.
  const blockHeight = `min(${(listHeight ?? 400) + HEADER_H}px, 55vh)`;

  return (
    <Body style={{ height: blockHeight }}>
      <BulkBarSlot $open={barOpen}>
        {renderBar && (
          <BulkBar role="toolbar" aria-label="Bulk actions">
            {confirmingBulk ? (
              <BulkConfirm role="alertdialog">
                <p>
                  Delete <strong>{shownCount}</strong>{' '}
                  {shownCount === 1 ? 'entry' : 'entries'}? This permanently
                  removes the selected term rules.
                </p>
                {bulkError && <ErrorText role="alert">{bulkError}</ErrorText>}
                <FormActions>
                  <GhostButton
                    type="button"
                    onClick={() => {
                      setBulkError(null);
                      setConfirmingBulk(false);
                    }}
                    disabled={bulkBusy}
                  >
                    Cancel
                  </GhostButton>
                  <DangerButton
                    type="button"
                    onClick={runBulkDelete}
                    disabled={bulkBusy}
                  >
                    Delete
                  </DangerButton>
                </FormActions>
              </BulkConfirm>
            ) : (
              <>
                <BulkCount>{shownCount} selected</BulkCount>
                <BulkActions>
                  <GhostButton type="button" onClick={handleExport}>
                    Export {shownCount}
                  </GhostButton>
                  <DangerOutlineButton
                    type="button"
                    onClick={() => {
                      setBulkError(null);
                      setConfirmingBulk(true);
                    }}
                  >
                    Delete {shownCount}
                  </DangerOutlineButton>
                </BulkActions>
              </>
            )}
          </BulkBar>
        )}
      </BulkBarSlot>
      <TableRegion>
        {hasRows ? (
          <TableVirtuoso
            // Remount when `initialItemCount` drops (PLAT-11199). Virtuoso pushes
            // `data` into its state stream BEFORE `initialItemCount` and
            // recomputes the row list on the `data` push alone, reading the
            // still-stale count imperatively. While the scroller is unmeasured
            // (every render up to the first ResizeObserver callback) that path
            // builds exactly `initialItemCount` rows straight off the array with
            // no clamp — so a shrink to fewer rows than the previous count yields
            // rows whose `data` is `undefined`, and `computeItemKey` throws
            // "Cannot read properties of undefined (reading 'id')". That is the
            // Translation Memory tab on reopen: the entries are cached, so the
            // table mounts with the full list and the source-language seed then
            // filters it down in the very next commit. Keying on the count means
            // a decrease mounts a fresh instance with both values consistent; an
            // increase, and any change above INITIAL_ROWS, is in-bounds and keeps
            // the instance (so scroll and measurements survive filtering a long
            // glossary).
            key={initialCount}
            data={entries}
            context={rowContext}
            components={VIRTUOSO_COMPONENTS}
            computeItemKey={(_index, entry) => entry.id}
            initialItemCount={initialCount}
            fixedHeaderContent={renderHeaderRow}
            itemContent={renderRow}
            totalListHeightChanged={setListHeight}
            style={{ flex: 1, minHeight: 0 }}
          />
        ) : (
          <TableScroll>
            <TableEl>
              <Colgroup showCollection={showCollection} />
              <thead>{renderHeaderRow()}</thead>
              <tbody>
                <tr>
                  <EmptyCell colSpan={columnCount}>
                    {status === 'loading' && 'Loading entries…'}
                    {status === 'ready' &&
                      !hasAnyEntries &&
                      `No entries in ${collectionName} yet. Add one to start building your glossary.`}
                    {status === 'ready' &&
                      hasAnyEntries &&
                      'No entries match your search or filters.'}
                    {status === 'error' && (
                      <span role="alert">
                        {error}{' '}
                        <button type="button" onClick={retry}>
                          Retry
                        </button>
                      </span>
                    )}
                  </EmptyCell>
                </tr>
              </tbody>
            </TableEl>
          </TableScroll>
        )}
      </TableRegion>
    </Body>
  );
};

export default EntryTable;
